/**
 * SaaS Spend Intelligence — world-first Total Cost of Feature (TCF).
 *
 * The killer surface is the TCF calculator: enter a feature slug + costs,
 * see the joined 4-source verdict (cloud + LLM + SaaS + engineer hours).
 * No competitor does this — Vendr, Sastrify, Cledara treat SaaS in a silo.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Layers, Calculator, AlertOctagon, Plus, Trash2,
  Sparkles, TrendingUp, DollarSign,
} from 'lucide-react';
import OktaConnectWizard from '../../components/OktaConnectWizard';

const API = process.env.REACT_APP_BACKEND_URL;

const VerdictPill = ({ verdict }) => {
  const map = {
    HEALTHY: { bg: 'bg-emerald-500/15', text: 'text-emerald-300', label: 'HEALTHY · 50%+' },
    PROFITABLE: { bg: 'bg-emerald-500/15', text: 'text-emerald-300', label: 'PROFITABLE · 20-50%' },
    THIN: { bg: 'bg-amber-500/15', text: 'text-amber-300', label: 'THIN · 0-20%' },
    LOSS_MAKING: { bg: 'bg-rose-500/15', text: 'text-rose-300', label: 'LOSS-MAKING · <0%' },
    AWAITING_REVENUE: { bg: 'bg-slate-500/15', text: 'text-slate-300', label: 'AWAITING REVENUE' },
  };
  const s = map[verdict] || map.AWAITING_REVENUE;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
};

const SeverityPill = ({ severity }) => {
  const map = {
    high: { bg: 'bg-rose-500/15', text: 'text-rose-300' },
    medium: { bg: 'bg-amber-500/15', text: 'text-amber-300' },
    low: { bg: 'bg-slate-500/15', text: 'text-slate-300' },
  };
  const s = map[severity] || map.medium;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${s.bg} ${s.text}`}>
      {severity}
    </span>
  );
};

export default function SaaSSpendIntelligence() {
  const [summary, setSummary] = useState(null);
  const [subs, setSubs] = useState([]);
  const [overlaps, setOverlaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tcf, setTcf] = useState(null);
  const [tcfBusy, setTcfBusy] = useState(false);
  const [tcfFields, setTcfFields] = useState({
    feature_slug: 'transcribe',
    cloud_cost_monthly_usd: 2400,
    llm_cost_monthly_usd: 800,
    saas_subscription_ids: [],
    engineer_hours_monthly: 4,
    engineer_hourly_rate_usd: 120,
    monthly_revenue_usd: 6000,
  });
  const [newSub, setNewSub] = useState({
    vendor: '', monthly_cost_usd: 0, seat_count: 1, seats_active_30d: 0, category: 'other',
  });

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    try {
      const [sRes, lRes, oRes] = await Promise.all([
        fetch(`${API}/api/saas-spend/summary`, { headers }),
        fetch(`${API}/api/saas-spend/subscriptions`, { headers }),
        fetch(`${API}/api/saas-spend/overlap/detect`, { headers }),
      ]);
      setSummary(await sRes.json());
      setSubs(await lRes.json());
      setOverlaps(await oRes.json());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const runTCF = async () => {
    setTcfBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/saas-spend/tcf/compute`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(tcfFields),
      });
      setTcf(await r.json());
    } finally { setTcfBusy(false); }
  };

  const addSubscription = async () => {
    if (!newSub.vendor || !newSub.monthly_cost_usd) return;
    const token = localStorage.getItem('token');
    await fetch(`${API}/api/saas-spend/subscriptions`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(newSub),
    });
    setNewSub({ vendor: '', monthly_cost_usd: 0, seat_count: 1, seats_active_30d: 0, category: 'other' });
    refresh();
  };

  const deleteSubscription = async (id) => {
    const token = localStorage.getItem('token');
    await fetch(`${API}/api/saas-spend/subscriptions/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    refresh();
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px"><div className="max-w-6xl mx-auto px-6 py-8" data-testid="saas-spend-page">
      {/* Header — vibrant hero */}
      <div className="mb-10">
        <div className="flex items-start gap-4 mb-4">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-500 items-center justify-center shadow-xl shadow-emerald-500/40 shrink-0">
            <Layers className="w-7 h-7 text-white drop-shadow-sm" />
          </div>
          <div className="flex-1">
            <Badge className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] font-mono tracking-widest mb-2">
              WORLD-FIRST
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 drop-shadow-[0_2px_18px_rgba(16,185,129,0.25)]">
              <span className="text-white">SaaS Spend </span><span className="text-emerald-400">Intelligence</span>
            </h1>
          </div>
        </div>
        <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">
          The only platform that <span className="text-emerald-300 font-semibold">joins SaaS subscriptions to cloud cost to LLM cost</span> to give you Total Cost of Feature. Vendr, Sastrify, Cledara treat SaaS in a silo. <span className="text-slate-900 font-semibold">We don't.</span>
        </p>
      </div>

      {/* Headline metrics — vibrant gradient KPI cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="saas-summary-cards">
          <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
            <CardContent className="p-5">
              <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Subscriptions</div>
              <div className="text-3xl font-bold text-white tabular-nums">{summary.subscription_count}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
            <CardContent className="p-5">
              <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Monthly Spend</div>
              <div className="text-3xl font-bold text-white tabular-nums">${summary.total_monthly_spend_usd.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-950 text-slate-100 border-amber-400/30">
            <CardContent className="p-5">
              <div className="text-[10px] font-bold text-amber-300 uppercase tracking-widest mb-2">Wasted on Unused Seats</div>
              <div className="text-3xl font-bold text-amber-200 tabular-nums">${summary.total_unused_seat_monthly_cost_usd.toLocaleString()}<span className="text-xs text-amber-300/70">/mo</span></div>
            </CardContent>
          </Card>
          <Card className="bg-slate-950 text-slate-100 border-emerald-400/30">
            <CardContent className="p-5">
              <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2">Overlap Savings (Annual)</div>
              <div className="text-3xl font-bold text-emerald-200 tabular-nums">${(summary.overlap_estimated_annual_savings_usd || 0).toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Okta Connect Wizard — auto-discover SaaS from Okta SSO */}
      <div className="mb-8">
        <OktaConnectWizard onSyncComplete={refresh} />
      </div>

      {/* TCF (Total Cost of Feature) — the hero widget */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/30 mb-8" data-testid="tcf-card">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Calculator className="w-5 h-5 text-emerald-400" />
            Total Cost of Feature (TCF) · 4-source join
            <Badge className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9px]">WORLD-FIRST</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">Feature slug</label>
                <input type="text" value={tcfFields.feature_slug}
                  onChange={(e) => setTcfFields({ ...tcfFields, feature_slug: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                  data-testid="tcf-feature-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">Cloud $/mo</label>
                  <input type="number" value={tcfFields.cloud_cost_monthly_usd}
                    onChange={(e) => setTcfFields({ ...tcfFields, cloud_cost_monthly_usd: Number(e.target.value) })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
                </div>
                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">LLM $/mo</label>
                  <input type="number" value={tcfFields.llm_cost_monthly_usd}
                    onChange={(e) => setTcfFields({ ...tcfFields, llm_cost_monthly_usd: Number(e.target.value) })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">Engineer hours/mo</label>
                  <input type="number" value={tcfFields.engineer_hours_monthly}
                    onChange={(e) => setTcfFields({ ...tcfFields, engineer_hours_monthly: Number(e.target.value) })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
                </div>
                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-widest mb-1 block font-semibold">Monthly revenue</label>
                  <input type="number" value={tcfFields.monthly_revenue_usd}
                    onChange={(e) => setTcfFields({ ...tcfFields, monthly_revenue_usd: Number(e.target.value) })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
                </div>
              </div>
              <Button onClick={runTCF} disabled={tcfBusy} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="run-tcf-btn">
                {tcfBusy ? 'Computing…' : 'Compute Total Cost of Feature'}
              </Button>
            </div>
            <div>
              {tcf ? (
                <div className="rounded-lg border border-slate-700 bg-slate-950 p-4 h-full">
                  <div className="flex items-start justify-between mb-3">
                    <div className="text-sm text-slate-400">{tcf.headline}</div>
                    <VerdictPill verdict={tcf.verdict} />
                  </div>
                  <div className="border-t border-slate-700 pt-3">
                    <div className="text-[10px] font-bold tracking-widest text-cyan-300 uppercase mb-2">Cost breakdown</div>
                    <div className="space-y-1 font-mono text-xs">
                      <div className="flex justify-between text-slate-400"><span>Cloud</span><span className="text-cyan-300">${tcf.breakdown.cloud_usd.toLocaleString()}/mo</span></div>
                      <div className="flex justify-between text-slate-400"><span>LLM</span><span className="text-cyan-300">${tcf.breakdown.llm_usd.toLocaleString()}/mo</span></div>
                      <div className="flex justify-between text-slate-400"><span>SaaS</span><span className="text-cyan-200">${tcf.breakdown.saas_usd.toLocaleString()}/mo</span></div>
                      <div className="flex justify-between text-slate-400"><span>Engineer ({tcf.breakdown.engineer_hours}h × ${tcf.breakdown.engineer_hourly_rate_usd}/hr)</span><span className="text-amber-300">${tcf.breakdown.engineer_usd.toLocaleString()}/mo</span></div>
                      <div className="flex justify-between text-white font-bold border-t border-slate-700 pt-1 mt-1"><span>TOTAL COST</span><span>${tcf.total_monthly_cost_usd.toLocaleString()}/mo</span></div>
                      {tcf.margin_pct !== null && (
                        <div className={`flex justify-between font-bold ${tcf.monthly_margin_usd >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}><span>MARGIN</span><span>{tcf.monthly_margin_usd >= 0 ? '+' : '−'}${Math.abs(tcf.monthly_margin_usd).toLocaleString()} ({tcf.margin_pct.toFixed(1)}%)</span></div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-slate-700 bg-slate-950 p-6 h-full flex flex-col items-center justify-center text-center">
                  <Sparkles className="w-8 h-8 text-emerald-400/60 mb-3" />
                  <p className="text-sm text-slate-400">Enter feature costs and click <span className="text-emerald-300">Compute TCF</span> for the world-first 4-source join: cloud + LLM + SaaS + engineer-hours = total feature cost.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overlap findings */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/20 mb-8">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <AlertOctagon className="w-5 h-5 text-amber-400" />
            SaaS ↔ Cloud Overlap Findings
            <span className="text-xs text-cyan-300 font-normal">({overlaps?.findings?.length || 0})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(!overlaps || overlaps.findings.length === 0) ? (
            <div className="text-center py-6 text-sm text-slate-400">
              No overlaps detected. Add SaaS subscriptions tagged by category (e.g. <code className="text-slate-400">observability</code>) to surface duplicate spend.
            </div>
          ) : (
            <div className="space-y-3">
              {overlaps.findings.map((f, i) => (
                <div key={i} className="rounded-lg border border-emerald-500/20 bg-slate-950 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-sm font-bold text-white">{f.tag.replace(/_/g, ' ').toUpperCase()}</div>
                    <div className="flex items-center gap-2">
                      <SeverityPill severity={f.severity} />
                      <span className="text-emerald-300 text-sm font-mono font-bold">+${f.estimated_monthly_savings_usd.toLocaleString()}/mo possible</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mb-2">{f.reason}</p>
                  <div className="text-xs text-slate-400">
                    <span className="font-bold text-slate-400">SaaS paid:</span>{' '}
                    {f.subscriptions.map((s) => `${s.vendor} ($${s.monthly_cost_usd}/mo)`).join(' · ')}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    <span className="font-bold text-slate-400">Cloud overlap:</span>{' '}
                    {f.cloud_services_overlapping.join(' · ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscriptions */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
          <CardHeader><CardTitle className="text-lg text-white flex items-center gap-2"><Plus className="w-4 h-4 text-emerald-400" />Add subscription</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              <input type="text" placeholder="Vendor (e.g., Datadog)" value={newSub.vendor}
                onChange={(e) => setNewSub({ ...newSub, vendor: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" placeholder="Monthly $" value={newSub.monthly_cost_usd || ''}
                  onChange={(e) => setNewSub({ ...newSub, monthly_cost_usd: Number(e.target.value) })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
                <select value={newSub.category}
                  onChange={(e) => setNewSub({ ...newSub, category: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white">
                  <option value="observability">Observability</option>
                  <option value="ai-tooling">AI tooling</option>
                  <option value="security">Security</option>
                  <option value="data-pipelines">Data pipelines</option>
                  <option value="productivity">Productivity</option>
                  <option value="infra">Infra</option>
                  <option value="crm">CRM</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" placeholder="Total seats" value={newSub.seat_count || ''}
                  onChange={(e) => setNewSub({ ...newSub, seat_count: Number(e.target.value) })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
                <input type="number" placeholder="Active seats (30d)" value={newSub.seats_active_30d || ''}
                  onChange={(e) => setNewSub({ ...newSub, seats_active_30d: Number(e.target.value) })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white" />
              </div>
              <Button onClick={addSubscription} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">Add</Button>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
          <CardHeader><CardTitle className="text-lg text-white flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-400" />Subscriptions ({subs.length})</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-12 bg-slate-800 rounded animate-pulse" />)}</div>
            ) : subs.length === 0 ? (
              <div className="text-center py-6 text-sm text-slate-400">
                No subscriptions yet. Add Datadog + Snyk + Cursor to see overlap detection live.
              </div>
            ) : (
              <div className="space-y-2">
                {subs.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-3 rounded-md border border-emerald-500/20 bg-slate-950 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{s.vendor}</div>
                      <div className="text-xs text-slate-400">
                        ${s.monthly_cost_usd}/mo · {s.utilisation_pct}% utilised · {s.category}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => deleteSubscription(s.id)} className="h-8 px-2 text-rose-400 hover:text-rose-300">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
}
